import type { Router, RouteRecordRaw } from "vue-router";
import { loadRemote, registerRemotes } from "@module-federation/runtime";
// Relative (not @/) import: this module is MF-exposed as ./remoteRegistry and
// its emitted d.ts ships to consumers, where the producer's @/ can't resolve.
import { defaultRemotes, type RemoteConfig } from "./remotes.config";
import { legacyRouterBridge, parentRouteNameFor } from "./vue-router-shim";
import { LegacyRemoteMount } from "../legacy";

// The remote catalog is code-defined (see remotes.config.ts). Consumers of
// the ./remoteRegistry expose (remote dev harnesses) can replace it — e.g.
// swap their own entry to their local origin — via configureRemotes().
let configuredRemotes: RemoteConfig[] | null = null;

export function configureRemotes(remotes: RemoteConfig[]) {
  configuredRemotes = remotes;
  remotesRegistered = false;
}

/** The catalog in force: configureRemotes() input, else the code default. */
export function getConfiguredRemotes(): RemoteConfig[] {
  return configuredRemotes ?? defaultRemotes;
}

export { defaultRemotes, applyRemoteEntryOverrides, type RemoteConfig } from "./remotes.config";

/** Shape of a remote's ./routes module — routes only. */
interface RemoteRoutesModule {
  routes?: RouteRecordRaw[];
  default?: RouteRecordRaw[];
}

/**
 * Shape of a remote's ./i18nMessages module. Keys must be namespaced by the
 * remote itself (e.g. `checklist.*`) — the host merges them as-is so the
 * remote's own t() calls work in both routes-mode and AppMount.
 */
interface RemoteI18nModule {
  i18nMessages?: Record<string, Record<string, unknown>>;
}

interface RemoteRoutesPayload {
  routes: RouteRecordRaw[];
  i18nMessages?: RemoteI18nModule["i18nMessages"];
}

interface RemoteRouteRegistryEntry {
  id: string;
  routePrefixes: string[];
  preload?: boolean;
  requiresAuthByDefault?: boolean;
  requiresPermission?: string;
  remoteName: string;
  alias?: string;
  mode?: RemoteConfig["mode"];
  loadRoutes: () => Promise<RemoteRoutesPayload>;
}

/**
 * Minimal surface the registry needs from the host i18n instance, avoids
 * coupling to vue-i18n's generic parameters (createI18n's return type is
 * narrowed by its messages schema).
 */
interface HostI18n {
  global: {
    mergeLocaleMessage: (locale: string, messages: Record<string, unknown>) => void;
  };
}

let hostI18n: HostI18n | undefined;

/**
 * Called once during bootstrap (this host's main.ts, or a remote dev harness's)
 * so remote locale messages can be merged into the consumer's i18n singleton.
 */
export function registerI18n(i18n: HostI18n) {
  hostI18n = i18n;
}

function mergeRemoteMessages(messages: RemoteI18nModule["i18nMessages"]) {
  if (!messages || !hostI18n) {
    return;
  }

  for (const [locale, localeMessages] of Object.entries(messages)) {
    hostI18n.global.mergeLocaleMessage(locale, localeMessages);
  }
}

type RemoteRouteLoadStatus = "not-matched" | "loaded-now" | "already-loaded" | "failed";
type EnsureRemoteRouteEntryLoadResult = "loaded-now" | "already-loaded" | "failed";

let remotesRegistered = false;

// --- Load-failure resilience (ported from MerchantApp mfeMixin) ---
// On first failure: invalidate the remote's MF cache and reload the page once
// (guarded by sessionStorage). On second failure: surface the error to the router.

function reloadKeyFor(remoteName: string) {
  return `mfe-reload:${remoteName}`;
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* private browsing */
  }
}

function safeSessionRemove(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* private browsing */
  }
}

export function clearRemoteReloadGuard(remoteName: string) {
  safeSessionRemove(reloadKeyFor(remoteName));
}

export function clearAllRemoteReloadGuards() {
  for (const remote of getConfiguredRemotes()) {
    clearRemoteReloadGuard(remote.remoteName);
  }
}

function invalidateRemote(entry: RemoteRouteRegistryEntry) {
  const remote = getConfiguredRemotes().find((r) => r.id === entry.id);
  if (!remote) {
    return;
  }

  registerRemotes(
    [
      {
        name: remote.remoteName,
        alias: remote.alias,
        entry: remote.entry,
        type: remote.entryType || "module",
      },
    ],
    { force: true },
  );
}

/**
 * Returns true when a cache-invalidating page reload was triggered (caller
 * should stop, the page is about to reload). Returns false when the reload
 * has already been attempted once; the failure should surface to the user.
 */
function handleLoadFailure(entry: RemoteRouteRegistryEntry): boolean {
  const reloadKey = reloadKeyFor(entry.remoteName);
  const reloadCount = Number.parseInt(safeSessionGet(reloadKey) || "0", 10);

  if (reloadCount < 1) {
    safeSessionSet(reloadKey, "1");
    // sessionStorage unavailable (private browsing), reloading would loop forever
    if (safeSessionGet(reloadKey) !== "1") {
      return false;
    }
    console.warn(`[remote-registry] Invalidating cache and reloading for "${entry.remoteName}"`);
    invalidateRemote(entry);
    window.location.reload();
    return true;
  }

  safeSessionRemove(reloadKey);
  return false;
}

function mapRemoteToRegistryEntry(remote: RemoteConfig): RemoteRouteRegistryEntry {
  return {
    id: remote.id,
    routePrefixes: remote.routePrefixes,
    preload: remote.preload,
    requiresAuthByDefault: remote.requiresAuthByDefault,
    requiresPermission: remote.requiresPermission,
    remoteName: remote.remoteName,
    alias: remote.alias,
    mode: remote.mode,
    loadRoutes: async () => {
      const remoteIdentifier = remote.alias || remote.remoteName;

      if (remote.mode === "legacy") {
        return { routes: legacyMountRoutes(remote, remoteIdentifier) };
      }

      // Expose names are the CONTRACT, not per-remote config: every routes-mode
      // remote publishes exactly ./routes and ./i18nMessages. Fetch both in
      // parallel. i18n is OPTIONAL and must never take routes down with it: a
      // remote may legitimately ship no locales, and older remotes predate the
      // ./i18nMessages expose entirely (they carried messages on ./routes).
      // So its rejection degrades to "no messages", not a failed remote.
      const [routesModule, i18nModule] = await Promise.all([
        loadRemote<RouteRecordRaw[] | RemoteRoutesModule>(`${remoteIdentifier}/routes`),
        loadRemote<RemoteI18nModule>(`${remoteIdentifier}/i18nMessages`).catch((error: unknown) => {
          console.warn(
            `[remote-registry] Remote "${remote.id}" exposes no usable ./i18nMessages; continuing without its locale messages:`,
            error,
          );
          return null;
        }),
      ]);

      if (!routesModule) {
        throw new Error(
          `[remote-registry] Remote "${remote.id}" returned an empty ./routes module`,
        );
      }

      const i18nMessages = i18nModule?.i18nMessages;

      if (Array.isArray(routesModule)) {
        return { routes: routesModule, i18nMessages };
      }

      // Prefer `default`: remotes export their ROOTED route array there
      // (createAppRoutes(basePath)); a named `routes` export may be the
      // remote's un-prefixed internal children.
      return {
        routes: routesModule.default ?? routesModule.routes ?? [],
        i18nMessages,
      };
    },
  };
}

/**
 * Legacy (AppMount) remote: nothing is fetched up front. One catch-all route
 * per prefix renders LegacyRemoteMount, which loads `<remote>/AppMount` and
 * calls its mount() with the MerchantApp-era host args. The remote's own
 * routes get grafted under these records by the legacy router bridge when
 * its bootloader calls createRouter().
 */
function legacyMountRoutes(remote: RemoteConfig, remoteIdentifier: string): RouteRecordRaw[] {
  const registration = { id: remote.id, routePrefixes: remote.routePrefixes };
  legacyRouterBridge.registerLegacyRemote(registration);

  return remote.routePrefixes.flatMap((prefix, index) => {
    const mount = {
      component: LegacyRemoteMount,
      props: {
        remoteId: remote.id,
        remoteName: remote.remoteName,
        mountPath: `${remoteIdentifier}/AppMount`,
        mountOptions: remote.mountOptions,
      },
      // legacyRemoteId keys the host's <RouterView>: switching from one legacy
      // remote's prefix to another's must REMOUNT LegacyRemoteMount (same
      // component, new props would only patch it, leaving the first remote's app
      // mounted and rendering the second remote's grafted records).
      meta: { scrollableContent: true, legacyRemoteId: remote.id },
    };
    const parentName = parentRouteNameFor(registration, index);
    return [
      // The graft target: exact prefix, NO params. vue-router requires a child
      // with an absolute path to repeat its parent's params and warns otherwise
      // (VUE_ROUTER_R0104), and the remote's rooted records are absolute, so the
      // record they hang from must be param-free.
      { path: prefix, name: parentName, ...mount },
      // Sub-paths before the remote has grafted its records (hard load of
      // /Customers/123): a catch-all that renders the same mount component.
      // Once grafted, the remote's more specific records outscore it. Both
      // records resolve to one LegacyRemoteMount instance: same component at
      // the same view depth, so the switch patches rather than remounts.
      { path: `${prefix}/:pathMatch(.*)*`, name: `${parentName}__subpaths`, ...mount },
    ];
  });
}

function getRemoteRouteRegistry(): RemoteRouteRegistryEntry[] {
  return getConfiguredRemotes().map(mapRemoteToRegistryEntry);
}

export function ensureRemotesRegistered() {
  if (remotesRegistered) {
    return;
  }

  const remotes = getConfiguredRemotes();
  registerRemotes(
    remotes.map((remote) => ({
      name: remote.remoteName,
      alias: remote.alias,
      entry: remote.entry,
      type: remote.entryType || "module",
    })),
  );

  remotesRegistered = true;
}

const loadedRemoteRouteIds = new Set<string>();
const remoteRouteLoadPromises = new Map<string, Promise<boolean>>();
const remoteRouteNamesByRemoteId = new Map<string, Set<string>>();

// Case-insensitive, like vue-router's default route matching (`sensitive: false`):
// the catalog says "/Customers", users type "/customers".
function pathMatchesPrefix(path: string, prefix: string) {
  const p = path.toLowerCase();
  const pre = prefix.toLowerCase();
  return p === pre || p.startsWith(`${pre}/`);
}

function findRemoteByPath(path: string) {
  return getRemoteRouteRegistry().find((entry) =>
    entry.routePrefixes.some((prefix) => pathMatchesPrefix(path, prefix)),
  );
}

/**
 * Reports routes a remote returned that sit outside every prefix the catalog
 * declares for it.
 *
 * The prefix is declared twice by necessity — here (so the host knows which
 * remote to lazily fetch for a URL, before any remote code has run) and in the
 * remote's own rooted routes. Nothing can reconcile them at host build time,
 * because the remote is a runtime artifact the host never compiles against.
 * Load time is therefore the earliest possible signal, and without it a stale
 * prefix presents as an unexplained 404: the host routes the URL here, fetches
 * the remote successfully, then finds no route matching the URL it started from.
 *
 * Deliberately does not throw. Navigation to the declared prefix fails either
 * way, so refusing to register would convert a partial failure — the remote's
 * real paths still work — into a total one.
 *
 * Uses the same matcher as findRemoteByPath so the check and the routing it
 * describes cannot disagree; notably "/" matches only an exact "/", which is
 * what makes the host-as-remote catalog entry pass.
 */
function reportRoutePrefixDrift(entry: RemoteRouteRegistryEntry, routes: RouteRecordRaw[]) {
  const strays = routes
    .map((route) => route.path)
    .filter(
      (path) =>
        typeof path === "string" &&
        path.startsWith("/") &&
        !entry.routePrefixes.some((prefix) => pathMatchesPrefix(path, prefix)),
    );

  if (strays.length === 0) {
    return;
  }

  console.error(
    `[remote-registry] route-prefix drift for "${entry.id}": catalog declares ` +
      `${entry.routePrefixes.map((p) => `"${p}"`).join(", ")} but the remote returned ` +
      `${strays.map((p) => `"${p}"`).join(", ")}. Navigation to the declared prefix will 404. ` +
      `Align the catalog entry in remotes.config.ts with the remote's rooted routes ` +
      `(and its SideNav entry in features/layout/menu.ts).`,
  );
}

function registerLoadedRouteName(remoteId: string, routeName?: string) {
  if (!routeName) {
    return;
  }

  const existingRouteNames = remoteRouteNamesByRemoteId.get(remoteId);
  if (existingRouteNames) {
    existingRouteNames.add(routeName);
    return;
  }

  remoteRouteNamesByRemoteId.set(remoteId, new Set([routeName]));
}

// Routes where a resilience page-reload must never fire: reloading during the
// OIDC callback replays an already-consumed code/state ("No matching state
// found in storage") and traps the app in a login loop.
const RELOAD_UNSAFE_PATHS = ["/signin-callback", "/login"];

function isReloadSafeLocation(): boolean {
  const path = globalThis.location.pathname;
  // Deploys under a subpath (PR previews / SWA routes) prefix pathname with
  // BASE_URL, so the unsafe paths must be checked relative to that base too.
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return !RELOAD_UNSAFE_PATHS.some((unsafe) => {
    const prefixedUnsafe = `${base}${unsafe}`;
    return (
      path === unsafe ||
      path.startsWith(`${unsafe}/`) ||
      path === prefixedUnsafe ||
      path.startsWith(`${prefixedUnsafe}/`)
    );
  });
}

async function ensureRemoteRouteEntryLoaded(
  entry: RemoteRouteRegistryEntry,
  router: Router,
  options: { allowReload?: boolean } = {},
): Promise<EnsureRemoteRouteEntryLoadResult> {
  const allowReload = options.allowReload ?? false;

  if (loadedRemoteRouteIds.has(entry.id)) {
    return "already-loaded";
  }

  const existingPromise = remoteRouteLoadPromises.get(entry.id);
  if (existingPromise) {
    const loaded = await existingPromise;
    return loaded ? "loaded-now" : "failed";
  }

  const loadPromise = entry
    .loadRoutes()
    .then(({ routes, i18nMessages }) => {
      mergeRemoteMessages(i18nMessages);
      reportRoutePrefixDrift(entry, routes);
      routes.forEach((route) => {
        const routeName = typeof route.name === "string" ? route.name : undefined;
        if (routeName && router.hasRoute(routeName)) {
          // Re-registration by the same remote (resilience reload) is expected
          // and stays quiet. A name owned by another remote or the shell is a
          // real collision the remote team must resolve by renaming; warn on
          // every load and never claim the name into this remote's ledger, so
          // the ledger only ever holds names this remote actually registered.
          if (!remoteRouteNamesByRemoteId.get(entry.id)?.has(routeName)) {
            console.warn(
              `[ensureRemoteRouteEntryLoaded] Route name "${routeName}" from remote "${entry.id}" collides with an already-registered route; skipping it. Rename the route in the remote to make it reachable.`,
            );
          }
          return;
        }

        router.addRoute({
          ...route,
          meta: {
            requiresAuth: entry.requiresAuthByDefault ?? true,
            ...(entry.requiresPermission ? { requiresPermission: entry.requiresPermission } : {}),
            ...route.meta,
          },
        });

        registerLoadedRouteName(entry.id, routeName);
      });

      loadedRemoteRouteIds.add(entry.id);
      remoteRouteLoadPromises.delete(entry.id);
      clearRemoteReloadGuard(entry.remoteName);
      return true;
    })
    .catch((error) => {
      console.error(`[ensureRemoteRouteEntryLoaded] Failed to load ${entry.id}/routes:`, error);
      remoteRouteLoadPromises.delete(entry.id);
      if (allowReload && isReloadSafeLocation()) {
        handleLoadFailure(entry);
      }
      return false;
    });

  remoteRouteLoadPromises.set(entry.id, loadPromise);
  const loaded = await loadPromise;
  return loaded ? "loaded-now" : "failed";
}

export async function ensureRemoteRoutesForPath(
  path: string,
  router: Router,
): Promise<RemoteRouteLoadStatus> {
  ensureRemotesRegistered();
  const remoteEntry = findRemoteByPath(path);

  if (!remoteEntry) {
    return "not-matched";
  }

  // Navigation into a remote's prefix is the only place the resilience
  // reload may fire (mirrors MerchantApp's route-entry-only reload).
  const loadResult = await ensureRemoteRouteEntryLoaded(remoteEntry, router, {
    allowReload: true,
  });

  if (loadResult === "failed") {
    return "failed";
  }

  return loadResult === "loaded-now" ? "loaded-now" : "already-loaded";
}

export async function preloadRemoteRoutes(router: Router) {
  ensureRemotesRegistered();
  const preloadEntries = getRemoteRouteRegistry().filter((entry) => entry.preload);
  await Promise.all(preloadEntries.map((entry) => ensureRemoteRouteEntryLoaded(entry, router)));
}

/**
 * Registers every legacy remote's prefix route up front. Legacy entries fetch
 * nothing (one LegacyRemoteMount record per prefix), so there is no reason to
 * wait for the first navigation into the prefix. Call it BEFORE `app.use(router)`:
 * the initial location and every <RouterLink> to a legacy prefix then resolve
 * against a registered record instead of tripping vue-router's "No match found"
 * warning (VUE_ROUTER_R0004). Routes-mode remotes still load on demand.
 */
export async function registerLegacyRemoteRoutes(router: Router) {
  ensureRemotesRegistered();
  const legacyEntries = getRemoteRouteRegistry().filter((entry) => entry.mode === "legacy");
  await Promise.all(legacyEntries.map((entry) => ensureRemoteRouteEntryLoaded(entry, router)));
}
